import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Bed, Bath, Ruler, Calendar, MapPin, Building2, X, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import IdxDisclaimer from "../components/IdxDisclaimer";
import SaveHeart from "../components/SaveHeart";
import JsonLd from "../components/JsonLd";

const fmt = (n) => (n ? `$${Number(n).toLocaleString()}` : "—");

export default function MlsListingDetail() {
  const { listingKey } = useParams();
  const [l, setL] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(null); // number | null

  useEffect(() => {
    setLoading(true);
    api.get(`/mls/listings/${listingKey}`)
      .then((r) => setL(r.data))
      .catch(() => setL(null))
      .finally(() => setLoading(false));
  }, [listingKey]);

  // Keyboard nav in lightbox
  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      const photos = l?.photos || [];
      if (e.key === "Escape") setLightboxIdx(null);
      else if (e.key === "ArrowRight") setLightboxIdx((i) => (i + 1) % photos.length);
      else if (e.key === "ArrowLeft") setLightboxIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, l]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>;
  if (!l) return (
    <div className="max-w-lg mx-auto mt-24 p-10 bg-white border border-gray-200 rounded-xl text-center">
      <div className="font-display text-2xl mb-2">Listing not found</div>
      <Link to="/mls-listings" className="text-[#E63946] hover:underline text-sm">← Back to listings</Link>
    </div>
  );

  const photos = l.photos || [];
  return (
    <div className="bg-stone-50 min-h-screen pb-24" data-testid="mls-listing-detail">
      <Seo
        title={`${l.address} — ${l.city}, ${l.state} · ${fmt(l.price)}`}
        description={`${l.beds} bed · ${l.baths} bath · ${l.sqft} sqft ${l.property_type} in ${l.city}, TX. Listed by ${l.list_office}. MLS #${l.listing_key}.`}
        path={`/mls-listings/${l.listing_key}`}
        image={photos[0]}
      />
      <JsonLd
        id={`ld-listing-${l.listing_key}`}
        data={{
          "@context": "https://schema.org",
          "@type": "SingleFamilyResidence",
          name: `${l.address}, ${l.city}, ${l.state}`,
          url: `https://gensrealty.com/mls-listings/${l.listing_key}`,
          image: photos.slice(0, 12),
          address: {
            "@type": "PostalAddress",
            streetAddress: l.address,
            addressLocality: l.city,
            addressRegion: l.state || "TX",
            postalCode: l.zip,
            addressCountry: "US",
          },
          numberOfRooms: l.beds,
          numberOfBathroomsTotal: l.baths,
          floorSize: l.sqft ? { "@type": "QuantitativeValue", value: l.sqft, unitCode: "FTK" } : undefined,
          yearBuilt: l.year_built,
          geo: (l.lat && l.lng) ? { "@type": "GeoCoordinates", latitude: l.lat, longitude: l.lng } : undefined,
          offers: {
            "@type": "Offer",
            price: l.price,
            priceCurrency: "USD",
            availability: l.status === "Active" ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
            seller: { "@type": "RealEstateAgent", name: l.list_office || "Gens Realty" },
          },
          identifier: { "@type": "PropertyValue", propertyID: "MLS", value: l.listing_key },
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6">
        <Link to="/mls-listings" className="text-sm text-gray-500 hover:text-[#E63946] flex items-center gap-1 mb-4" data-testid="mls-detail-back">
          <ArrowLeft className="w-4 h-4" /> Back to listings
        </Link>

        {/* Hero Gallery — main image + 4 preview thumbnails on the right */}
        <div className="grid grid-cols-4 gap-2 h-[480px] rounded-2xl overflow-hidden bg-white border border-gray-200 relative">
          <button
            type="button"
            onClick={() => photos.length > 0 && setLightboxIdx(photoIdx)}
            className="col-span-4 md:col-span-3 relative bg-stone-100 group"
            data-testid="mls-detail-hero-photo"
          >
            {photos[photoIdx] ? (
              <img src={photos[photoIdx]} alt={l.address} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">No photo</div>
            )}
          </button>
          <div className="hidden md:grid grid-cols-1 gap-2 overflow-y-auto">
            {photos.slice(0, 4).map((p, i) => (
              <button
                key={i}
                onClick={() => setPhotoIdx(i)}
                className={`bg-stone-100 h-[116px] overflow-hidden ${i === photoIdx ? 'ring-2 ring-[#E63946]' : ''}`}
                data-testid={`mls-photo-thumb-${i}`}
              >
                <img src={p} alt="" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform" />
              </button>
            ))}
          </div>
          {photos.length > 0 && (
            <>
              <SaveHeart
                listingKey={l.listing_key}
                address={l.address}
                className="absolute top-4 right-4 !w-10 !h-10"
              />
              <button
                onClick={() => setLightboxIdx(0)}
                className="absolute bottom-4 right-4 bg-white/95 backdrop-blur hover:bg-white text-[#1A1A1A] px-4 py-2.5 rounded-md text-xs font-semibold uppercase tracking-[0.15em] flex items-center gap-2 shadow-md"
                data-testid="mls-detail-viewall-photos"
              >
                <Images className="w-3.5 h-3.5" /> View all {photos.length} photos
              </button>
            </>
          )}
        </div>

        {/* Full gallery grid — every remaining photo */}
        {photos.length > 4 && (
          <div className="mt-3" data-testid="mls-detail-full-gallery">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {photos.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIdx(i)}
                  className="aspect-[4/3] bg-stone-100 overflow-hidden rounded-md group"
                  data-testid={`mls-full-gallery-${i}`}
                >
                  <img
                    src={p}
                    alt={`${l.address} photo ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
          <main className="lg:col-span-8 space-y-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E63946] mb-2">Active · MLS #{l.listing_key}</div>
              <h1 className="font-display text-3xl lg:text-5xl font-light tracking-tighter">{l.address}</h1>
              <div className="text-gray-500 mt-1">{l.city}, {l.state} {l.zip}</div>
              <div className="font-display text-4xl mt-4 text-[#E63946]">{fmt(l.price)}</div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat icon={<Bed className="w-4 h-4" />} label="Beds" value={l.beds} />
              <Stat icon={<Bath className="w-4 h-4" />} label="Baths" value={l.baths} />
              <Stat icon={<Ruler className="w-4 h-4" />} label="Sqft" value={l.sqft?.toLocaleString?.() || l.sqft} />
              <Stat icon={<Calendar className="w-4 h-4" />} label="Built" value={l.year_built || "—"} />
            </div>

            {l.remarks && (
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3">Listing description</div>
                <p className="text-[#333] leading-relaxed whitespace-pre-line">{l.remarks}</p>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-4">Details</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 text-sm">
                <Detail label="Property Type" value={l.property_type} />
                <Detail label="Status" value={l.status} />
                <Detail label="Days on Market" value={l.days_on_market} />
                <Detail label="Lot Size" value={l.lot_size ? `${l.lot_size} ac` : "—"} />
                <Detail label="City" value={l.city} />
                <Detail label="Zip" value={l.zip} />
              </div>
            </div>

            {l.lat && l.lng && (
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <iframe
                  title="Location map"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${l.lng - 0.01}%2C${l.lat - 0.008}%2C${l.lng + 0.01}%2C${l.lat + 0.008}&layer=mapnik&marker=${l.lat}%2C${l.lng}`}
                  className="w-full h-72 border-0"
                />
              </div>
            )}
          </main>

          <aside className="lg:col-span-4 space-y-4">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 sticky top-6">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-4 flex items-center gap-1"><Building2 className="w-3 h-3" /> Listed by</div>
              <div className="font-display text-lg font-semibold">{l.list_agent || "—"}</div>
              <div className="text-sm text-gray-500">{l.list_office || "—"}</div>

              <hr className="my-5 border-gray-100" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3">Want to see this home?</div>
              <Link
                to="/contact"
                state={{ subject: `Tour request — ${l.address} (MLS #${l.listing_key})` }}
                className="block bg-[#E63946] hover:bg-[#c9313d] text-white text-center py-3 rounded-md text-sm font-semibold mb-2"
                data-testid="mls-detail-contact"
              >
                Request a Showing
              </Link>
              <Link
                to="/pre-approval"
                className="block bg-[#1A1A1A] hover:bg-[#333] text-white text-center py-3 rounded-md text-sm font-semibold"
                data-testid="mls-detail-preapproval"
              >
                Get Pre-Approved
              </Link>
              {l.lat && l.lng && (
                <div className="mt-4 text-xs text-gray-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-[#E63946]" /> {l.lat.toFixed(4)}, {l.lng.toFixed(4)}
                </div>
              )}
            </div>
          </aside>
        </div>

        <div className="mt-16 text-[10px] uppercase tracking-[0.2em] text-gray-400 text-center leading-relaxed">
          IDX data © Houston Association of REALTORS® · Delivered via CoreLogic Trestle · Listing courtesy of {l.list_office}
        </div>
        <IdxDisclaimer listing={l} />
      </div>

      {/* Lightbox — full-screen photo viewer with keyboard nav */}
      {lightboxIdx !== null && photos.length > 0 && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
          onClick={() => setLightboxIdx(null)}
          data-testid="mls-detail-lightbox"
        >
          <div className="flex items-center justify-between px-6 py-4 text-white text-sm">
            <div>{lightboxIdx + 1} / {photos.length}</div>
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
              className="hover:text-[#E63946] flex items-center gap-2"
              data-testid="mls-lightbox-close"
            >
              <X className="w-5 h-5" /> Close
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 relative">
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i - 1 + photos.length) % photos.length); }}
              className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center"
              aria-label="Previous"
              data-testid="mls-lightbox-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <img
              src={photos[lightboxIdx]}
              alt={`${l.address} photo ${lightboxIdx + 1}`}
              className="max-h-[85vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxIdx((i) => (i + 1) % photos.length); }}
              className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center"
              aria-label="Next"
              data-testid="mls-lightbox-next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
          <div className="text-center text-white/60 text-xs pb-4">
            Press <kbd className="px-1.5 py-0.5 bg-white/10 rounded">←</kbd> / <kbd className="px-1.5 py-0.5 bg-white/10 rounded">→</kbd> to navigate · <kbd className="px-1.5 py-0.5 bg-white/10 rounded">Esc</kbd> to close
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-1">{icon} {label}</div>
      <div className="font-display text-2xl mt-1">{value ?? "—"}</div>
    </div>
  );
}
function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500">{label}</div>
      <div className="text-sm text-[#1A1A1A] mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
